import React from 'react';
import PropTypes from 'prop-types';
import isValid from 'date-fns/isValid';
import { Collapse, Grid } from '@material-ui/core';
import { policyType as PolicyType, paymentMethod as PaymentMethod } from '@ourbranch/lookups';
import classNames from 'classnames';

import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import { track } from 'core/helpers/analytics';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { Button } from 'core/components/button';
import home from 'core/assets/svg/home.svg';
import auto from 'core/assets/svg/auto.svg';
import farm from 'core/assets/svg/farm.svg';
import renters from 'core/assets/svg/renters.svg';
import notificationIconWhite from 'core/assets/svg/ic-info-white.svg';
import pencilIcon from 'core/assets/svg/pencil-orange.svg';
import condo from 'core/assets/svg/condo.svg';
import { LabelTooltip } from 'core/components/label-tooltip';
import { dateFormatter, numberFormatter, awsDateToDateObject } from 'core/helpers/formatters';
import useStyles from './price-detail.styles';

const PaymentSchedulesTooltipTitle = ({ nextPayments }) => {
  const classes = useStyles();
  return (
    <>
      <Label type="titleDollarLabel" className={classes.paymentSchedules}>
        Payment schedule:
      </Label>
      <div>
        {nextPayments.map(({ date, remainingMonthly, total }, i) => (
          <div key={i}>
            <Label
              type="titleDollarLabel"
              className={classes.tooltip}
            >{`$${total ?? remainingMonthly} on ${date}`}</Label>
          </div>
        ))}
      </div>
    </>
  );
};

PaymentSchedulesTooltipTitle.propTypes = {
  nextPayments: PropTypes.array.isRequired
};

const CoverageItem = ({ label, value }) => {
  const classes = useStyles();
  return (
    <div>
      <Label type="coverageLabel">{label}</Label>
      <Label type="coverageValue" className={classes.coverageValue}>
        {value}
      </Label>
    </div>
  );
};

CoverageItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

const iconMap = {
  Home: { icon: home, height: 40 },
  Auto: { icon: auto, height: 28 },
  Renters: { icon: renters, height: 56 },
  Condo: { icon: condo, height: 64 },
  Farm: { icon: farm, height: 56 }
};

const PriceDetail = ({
  name,
  type,
  isMonthly,
  downPaymentDate,
  remainingPayments,
  remainingPaymentsAmount,
  payment,
  paymentMethod,
  paymentType,
  termTotal,
  rentersTotalAR,
  autoTotalAR,
  open,
  allowCollapsableContent,
  policyType,
  nextPayments,
  standaloneRenters,
  megaDownPay,
  goToBillingDetails,
  effectiveDate
}) => {
  const classes = useStyles();
  const toast = useToast();
  const { isAgency } = useSession();
  const {
    offer: { currentTab }
  } = useStore();

  const isBundleRenters = policyType === PolicyType.ARBundle && !standaloneRenters;
  const isFarm = policyType === PolicyType.Farm;
  const isDirect = [PaymentMethod.Direct, PaymentMethod.BillToOther].includes(paymentMethod);

  const dateLabelFormatter = (date) => {
    if (isFarm && isDirect) {
      return 'INVOICED TODAY';
    }
    if (date === 'TODAY') {
      return 'DUE TODAY';
    }
    // an unparseable/missing date must degrade to no label, not throw into the error boundary;
    // empty string (not null) because the call site interpolates the result into a template literal
    const dateObject = date ? awsDateToDateObject(date) : null;
    if (!dateObject || !isValid(dateObject)) {
      return '';
    }
    return dateObject.toDateString() === new Date().toDateString() ? 'DUE TODAY' : `DUE ${dateFormatter(dateObject)}`;
  };

  return (
    <div>
      {name === 'Bundle' && (
        <Label type="policyLabel" className={classes.policy}>
          {type.toUpperCase()}
        </Label>
      )}
      <div className={classes.policyContainer}>
        <div className={classes.paymentContainer}>
          <Label type="titleDollarLabel">$</Label>
          <Label type="titleLight" className={classes.payment}>
            {numberFormatter(payment, 2)}
          </Label>
          <Label type="paymentLabel" className={classes.paymentDate}>
            {isMonthly || isDirect
              ? `${dateLabelFormatter(downPaymentDate)}`
              : type === 'Auto'
                ? '6 MONTHS'
                : '12 MONTHS'}
          </Label>
        </div>
        <img src={iconMap[type].icon} alt={`${type}-icon`} height={iconMap[type].height} />
      </div>
      {isMonthly && nextPayments && (
        <div className={classes.remainingPayments}>
          <div>
            Next <span className={classNames({ [classes.highlightedNumber]: megaDownPay })}>{remainingPayments}</span>{' '}
            payments of ${numberFormatter(remainingPaymentsAmount, 2)}
          </div>
          <LabelTooltip
            tooltip={{
              label: 'View Schedule',
              onHoverText: (
                <PaymentSchedulesTooltipTitle
                  nextPayments={nextPayments.map(({ remainingMonthly, total, date }) => {
                    return {
                      date: dateFormatter(awsDateToDateObject(date)),
                      remainingMonthly: numberFormatter(total ?? remainingMonthly, 2)
                    };
                  })}
                />
              )
            }}
            placement="bottom"
          />
        </div>
      )}
      <div className={classes.extendedInfo}>
        <div>
          <Label type="coverageLabel">Effective Date</Label>
          <Label type="coverageValue" className={classes.effectiveDate}>
            {dateFormatter(effectiveDate)}
            {goToBillingDetails && (
              <Button
                type="button"
                className={classes.editButton}
                onClick={() => {
                  goToBillingDetails();
                  track('Edit Effective Date click from price details', {
                    isAgency,
                    policyType,
                    currentTab
                  });
                }}
              >
                <img src={pencilIcon} alt="Edit Icon" className={classes.editIcon} />
              </Button>
            )}
          </Label>
        </div>
        {isMonthly && nextPayments && (
          <div>
            <CoverageItem label="Total Term Price" value={`$${termTotal}`} />
          </div>
        )}
      </div>
      <Collapse in={open || !allowCollapsableContent}>
        {megaDownPay && (
          <Card className={classes.megaDownPayCard}>
            <img src={notificationIconWhite} alt="Notification Icon" className={classes.icon} />
            <Grid container>
              <Grid item className={classes.header}>
                The down payment and second payment have been combined into a single payment.
              </Grid>
              <Grid item className={classes.text}>
                This results in a larger initial payment but fewer total payments. To reduce the initial payment, go
                edit your policy's{' '}
                <span
                  onClick={() => {
                    goToBillingDetails();
                    toast.notify({
                      type: 'success',
                      message:
                        'Choose a billing date that is 15 or more days in the future. Alternatively, you can choose to opt out combining payments if the option if available.'
                    });
                  }}
                  className={classes.inlineLink}
                >
                  billing settings
                </span>
                .
              </Grid>
            </Grid>
          </Card>
        )}
        <div className={classes.extendedInfo}>
          <CoverageItem label="Payment Method" value={paymentMethod} />
          <CoverageItem label="Payment Frequency" value={paymentType} />
          {isBundleRenters && (
            <>
              <CoverageItem label="Renters Cost" value={rentersTotalAR} />
              <CoverageItem label="Auto Cost" value={autoTotalAR} />
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

PriceDetail.propTypes = {
  open: PropTypes.bool,
  name: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  isMonthly: PropTypes.bool.isRequired,
  payment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  downPaymentDate: PropTypes.string,
  remainingPayments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  remainingPaymentsAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paymentMethod: PropTypes.string.isRequired,
  paymentType: PropTypes.string.isRequired,
  termTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  stale: PropTypes.bool,
  allowCollapsableContent: PropTypes.bool,
  rentersTotalAR: PropTypes.string,
  autoTotalAR: PropTypes.string,
  policyType: PropTypes.string.isRequired,
  nextPayments: PropTypes.array,
  standaloneRenters: PropTypes.bool,
  megaDownPay: PropTypes.bool,
  goToBillingDetails: PropTypes.func,
  effectiveDate: PropTypes.string.isRequired
};

PriceDetail.defaultProps = {
  downPaymentDate: null,
  remainingPayments: null,
  remainingPaymentsAmount: null,
  stale: false,
  allowCollapsableContent: false,
  open: false,
  rentersTotalAR: null,
  autoTotalAR: null,
  nextPayments: [],
  goToBillingDetails: null
};
export default PriceDetail;
